<script setup>
// Display hints and a wordhunt grid, dynamically generated based on word lengths.
import { ref, computed, onMounted } from 'vue';
const props = defineProps({
  gridLetters: Object,
  gridHeight: Number,
  gridLength: Number,
  showLetters: Boolean,
  minLength: Number,
  maxPoints: Number,
  type: String,
});

/* Words is an array of objects. Objects look like this:

{
  vertical: bool -- true is vertical, false is horiz
  position: { x: number, y: number} -- Starting position for the word
  word: string -- the word itself
  hint: string -- the clue displayed on the screen to help players solve the word
}

*/
const gridLength = computed(() => {
  return props.gridLength;
});
const gridHeight = computed(() => {
  return props.gridHeight;
});

const rules = computed(() => {
  const rules = [];
  switch (props.type) {
    default: break;
    case ('wordHunt'): {
      rules.push('<strong>Goal:</strong> Find as many words as possible before time runs out!');
      rules.push('<strong>Letters must be connected</strong> (Diagonal is OK)');
      rules.push(`Words must be <strong>${props.minLength} letters or longer</strong>`);
      rules.push('Each letter tile may only be used <strong>once per word</strong>');
      // rules.push(`Top team gets <strong>${props.maxPoints > 4 ? props.maxPoints : props.maxPoints * 2} points</strong>, second gets <strong>${props.maxPoints > 4 ? props.maxPoints - 1 : props.maxPoints *2 - 1} points</strong>, etc`);
      break;
    }
    case ('anagramScramble'): {
      // rules.push('<strong>Goal:</strong> Create as many words as possible before time runs out!');
      // rules.push(`Words must be <strong>${props.minLength} letters or longer</strong>`);
      // rules.push('Freely pick and choose any of the letters to use, but <br /> each letter tile may only be used <strong>once per word</strong>');
      // rules.push(`Top team gets <strong>${props.maxPoints > 4 ? props.maxPoints : props.maxPoints * 2} points</strong>, second gets <strong>${props.maxPoints > 4 ? props.maxPoints - 1 : props.maxPoints *2 - 1} points</strong>, etc`);
      break;
    }
  }
  return rules;
})

const wordsWithPositions = computed(() => {
  // Output array of words. Each entry is an array of objects (letters) with x/y coordinates
  const words = [];
  const startingGridPoints = []; // So we can check for duplicate starting points
  for (let a = 0; a < props.words.length; a += 1) {
    const word = [];
    const w = props.words[a];
    for (let b = 0; b < w.word.length; b += 1) {
      const gridPoint = {
        x: !w.vertical ? w.position.x : w.position.x + b,
        y: w.vertical ? w.position.y : w.position.y + b,
        letter: w.word[b],
        display: false,
        key: a,
        first: false, // First letter in sequence, so we can show key.
        intersects: null, // Two clues that start on the same tile. Put the shared key here
      }
      if (b === 0) gridPoint.first = true;
      const startingPoint = `${gridPoint.x} ${gridPoint.y}`;
      // Deal with two potential clues starting on same square.
      if (startingGridPoints.length && startingGridPoints.filter(x => x === startingPoint).length) {
        try {
          gridPoint.intersects = words.filter(w => `${w[0].x} ${w[0].y}` === startingPoint)[0][0].key;
        } catch {}
      } 
      startingGridPoints.push(startingPoint);
      word.push(gridPoint);
    }
    words.push(word);
  }
  return words;
});

// const grid = reactive({});
// const xPos = reactive({});
// const yPos = reactive({});


const squareDisplay = ref({});
const squareAnimate = ref({x: 0, y: 0});
const randomizedLetter = ref('A');
const interval = ref(null);

function randomLetter() {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  randomizedLetter.value = alphabet[Math.floor(Math.random() * alphabet.length)];
}

function squareContent(x, y) {
  const sq = { ...props.gridLetters[x][y] };
  switch (sq.letter) {
      default: break;
      case ('Q'): {
        sq.letter = 'Qu';
        break;
      }
      case ('1'): {
        sq.letter = 'Ch';
        break;
      }
      case ('2'): {
        sq.letter = 'St';
        break;
      }
      case ('3'): {
        sq.letter = 'Er';
        break;
      }
      case ('4'): {
        sq.letter = 'Sa';
        break;
      }
      case ('5'): {
        sq.letter = 'Tt';
        break;
      }
      case ('6'): {
        sq.letter = 'Ne';
        break;
      }
      case ('7'): {
        sq.letter = 'On';
        break;
      }
      case ('8'): {
        sq.letter = 'En';
        break;
      }
      case ('9'): {
        sq.letter = 'Ra';
        break;
      }
      case ('0'): {
        sq.letter = 'Mn';
        break;
      }
    }
  if (!squareDisplay.value[x] || !squareDisplay.value[x][y]) {
    return '_';
  }
  if (squareAnimate.value.x === x && squareAnimate.value.y === y) {
    return { letter: randomizedLetter.value };
  }
  return sq;
}

onMounted(() => {
  let timer = 500;
  for (let x = 0; x < gridLength.value; x += 1) {
    for (let y = 0; y < gridHeight.value; y += 1) {
      interval.value = setInterval(() => randomLetter(), 100);
      setTimeout(() => {
        squareAnimate.value.x = x;
        squareAnimate.value.y = y;
        if (!squareDisplay.value[x]) squareDisplay.value[x] = {};
        squareDisplay.value[x][y] = true;
        if (x === gridLength.value - 1 && y === gridHeight.value - 1) {
          // Stop randomizing after the last one
          setTimeout(() => {
            squareAnimate.value.x = null;
            squareAnimate.value.y = null;
            clearInterval(interval.value);
          }, 1200);
        }
      }, timer);
      timer += 1200;
    }
  }
})

</script>

<template>
  <div class="wordhunt__container">
    <!-- Display the puzzle -->
    <div
        class="wordhunt__td">
      <table
          class="wordhunt__puzzle">
        <tbody>
            <tr
              v-for="x in gridLength"
              :key="x"
              class="wordhunt__row">
            <td
                v-for="y in gridHeight"
                :key="y"
                class="wordhunt__square">
              <Transition
                  name="fade">
                <span
                    :id="`letter-${x - 1}-${y - 1}`"
                    class="wordhunt__letter"
                    v-if="squareContent(x - 1, y - 1).letter"
                    v-text="squareContent(x - 1, y - 1).letter"
                />
              </Transition>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Display the sidebar -->
    <div
        class="wordhunt__td">
      <div
          v-for="(rule, index) in rules"
          :key="index"
          class="wordhunt__hint__container">
        <div
            class="wordhunt__hint"
            v-html="rule">
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wordhunt {
  &__td {
    margin: 0px auto;
  }
  &__container {
    display: flex;
  }
  &__puzzle {
    border-spacing: 0.6vmax;
    background: linear-gradient(to top, #2c98c7, #34b4eb);
    border: 9px solid gold;
    border-radius: 15px;
    padding: 10px;
  }
  &__square {
    color: darkslategrey;
    border-radius: 10px;
    background: #eae6ed;
	  box-shadow:
      inset -8px -6px 15px #778,
      inset 38px 18px 36px #fffffc,
      12px 8px 18px hsla(0,0%,0%,0.25);
    width: 6vw;
    height: 12vh;
    font-size: 1.5em;
    transition: all 1s ease;
  }
  &__hint {
    flex: 1;
    &__container {
      background-color: lighten(darkslategrey, 25%);
      display: flex;
      font-size: 0.5em;
      border-radius: 10px;
      margin: 2.5vh 0.5vw;
      padding: 1.75vh 0.25vw;
    }
  }
}
</style>
